import React, {
  PropsWithChildren,
  Children,
  isValidElement,
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useId,
} from 'react';
import isNil from 'lodash-es/isNil';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { LayoutGroup } from 'framer-motion';
import throttle from 'lodash-es/throttle';
import { semantic_colors } from '../../foundation';
import { Stack } from '../stack';

const MAX_CONTENT_WIDTH = 1024;
const SCROLL_FADE_WIDTH = 32;

export type TabWidth = 'fill' | 'auto';
export interface LineTabsContextProps {
  width?: TabWidth;
  paddingTop?: boolean;
  activeTabId?: string;
  onChange?: (id: string) => void;
}

export const LineTabContext = createContext<LineTabsContextProps | null>(null);

export const useLineTabContext = () => {
  const context = useContext(LineTabContext);

  if (!context) {
    throw new Error('LineTabContext is not defined');
  }
  return context;
};

export interface LineTabsProps {
  className?: string;
  /**
   * 'fill' 일경우 컨테이너 기준 Full Width 값, 'auto' 일경우 컨텐츠에 따라 자동 너비 계산됩니다.
   * @default 'auto'
   */
  width?: TabWidth;
  /**
   * 좁은 간격 사용 유무
   *  - width가 'auto' 일경우 탭아이템간 column-gap은 항상 0입니다.
   * @default false
   */
  dense?: boolean;
  /**
   * side padding on/off
   * @default true
   */
  paddingHorizontal?: boolean;
  /**
   * 탭 상단 여백 포함 여부
   * @default true
   */
  paddingTop?: boolean;
  /**
   * 탭 영역의 최대 너비를 1024px로 제한하고 가운데 정렬합니다.
   * @default false
   */
  hasMaxWidth?: boolean;
  activeTabId?: string;
  onChange?: (id: string) => void;
}

export type LineTabsComponentProps = PropsWithChildren<LineTabsProps>;

export const LineTabs = ({
  className,
  children,
  dense = false,
  paddingHorizontal = true,
  paddingTop = true,
  hasMaxWidth = false,
  width = 'auto',
  activeTabId,
  onChange,
}: LineTabsComponentProps) => {
  const id = useId();
  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);
  const [mask, setMask] = useState({ leftMask: 0, rightMask: 0 });
  const TAB_PADDING = paddingHorizontal ? (dense ? 16 : 28) : 0;

  const handleTabScroll = throttle(() => {
    updateMask();
  }, 500);

  const updateMask = () => {
    const scrollContainer = scrollWrapperRef.current;
    if (!scrollContainer) return;

    if (scrollContainer.scrollWidth > scrollContainer.clientWidth) {
      const showLeftMask = scrollContainer.scrollLeft > 0;
      const showRightMask = scrollContainer.scrollLeft + scrollContainer.offsetWidth < scrollContainer.scrollWidth;

      setMask({
        leftMask: showLeftMask ? 1 : 0,
        rightMask: showRightMask ? 1 : 0,
      });
    }
  };

  const updateActiveScrollPosition = (initial: boolean) => {
    // trigger: first render or mask open
    const scrollMaskOpen = mask.leftMask === 1 || mask.rightMask === 1;

    if (scrollMaskOpen || initial) {
      const scrollContainer = scrollWrapperRef.current;

      if (scrollContainer) {
        const tabContainerRect = scrollContainer.getBoundingClientRect();
        const filtered_children = Children.toArray(children).filter((child) => !isNil(child));

        Children.forEach(filtered_children, (child, index) => {
          if (!isValidElement(child)) return;
          if (child.props.id === activeTabId) {
            const tab = scrollContainer.children[index];
            const currentTabItemRect = tab.getBoundingClientRect();
            const correction =
              scrollContainer.scrollLeft - scrollContainer.clientWidth / 2 + currentTabItemRect.width / 2;

            scrollContainer.scrollTo({
              left: currentTabItemRect.left - tabContainerRect.left + correction,
              behavior: initial ? 'auto' : 'smooth',
            });
          }
        });
      }
    }
  };

  useEffect(() => {
    updateActiveScrollPosition(true);
    updateMask();
  }, []);

  useEffect(() => {
    updateActiveScrollPosition(false);
  }, [activeTabId]);

  return (
    <LineTabContext.Provider value={{ width, paddingTop, activeTabId, onChange }}>
      <TabContainer
        className={classNames('pds-line-tabs', className)}
        css={css`
          padding: 0 ${TAB_PADDING}px;
        `}
      >
        <IndicatorContainer
          direction='row'
          role='tablist'
          $leftMask={mask.leftMask}
          $rightMask={mask.rightMask}
          css={[
            css`
              ${hasMaxWidth && `max-width: ${MAX_CONTENT_WIDTH}px; justify-content: safe center;`}
              ${width === 'auto' && `column-gap: ${dense ? '16px' : '24px'}`}
            `,
          ]}
          onScroll={handleTabScroll}
          ref={scrollWrapperRef}
        >
          <LayoutGroup id={id}>{children}</LayoutGroup>
        </IndicatorContainer>
      </TabContainer>
    </LineTabContext.Provider>
  );
};

const TabContainer = styled.nav`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: inset 0 -1px 0 0 ${semantic_colors.border.primary};
`;

const IndicatorContainer = styled(Stack)<{ $leftMask: number; $rightMask: number }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0;
  overflow-x: scroll;
  scrollbar-width: none;
  mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 ${({ $leftMask }) => $leftMask * SCROLL_FADE_WIDTH}px,
    #000 calc(100% - ${({ $rightMask }) => $rightMask * SCROLL_FADE_WIDTH}px),
    transparent 100%
  );

  ::-webkit-scrollbar {
    display: none;
  }
`;
